import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CATEGORY_META, currency, num, LUXURY_IMAGES } from "@/lib/constants";
import OpportunityCard from "@/components/OpportunityCard";
import MapView from "@/components/MapView";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Zap, Building2, DollarSign } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell, PieChart, Pie } from "recharts";

const KPI = ({ label, value, sub, icon: Icon, accent = "#d4a574" }) => (
  <div className="kpi-frame rounded-lg p-4">
    <div className="flex items-start justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold">{label}</div>
      {Icon && <Icon size={14} style={{ color: accent }} />}
    </div>
    <div className="font-display text-3xl font-bold" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[11px] text-[#b8ac96] mt-1">{sub}</div>}
  </div>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [top, setTop] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/top-opportunities?limit=6").then((r) => setTop(r.data.items || []));
    api.get("/dashboard/recent?limit=8").then((r) => setRecent(r.data.items || []));
  }, []);

  const catData = (stats?.by_category || []).map(c => ({
    name: CATEGORY_META[c.category]?.label || c.category,
    count: c.count,
    color: CATEGORY_META[c.category]?.color || "#d4a574",
  })).sort((a, b) => b.count - a.count).slice(0, 8);

  const stateData = (stats?.by_state || []).slice(0, 10).map(s => ({ name: s.state, count: s.count, score: Math.round(s.avg_score) }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Hero header */}
      <div className="relative rounded-lg overflow-hidden border border-[#33291f]" data-testid="dashboard-hero">
        <img src={LUXURY_IMAGES[0]} className="absolute inset-0 w-full h-full object-cover opacity-30" alt="" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#14100c] via-[#14100c]/80 to-[#14100c]/40" />
        <div className="relative p-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Distress Intelligence · Today</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
            <span className="text-[#f4ede2]">{num(stats?.total_opportunities || 0)}</span> live opportunities.
            <br /><span className="italic text-[#d4a574] font-normal">Ranked by AI. Ready to move on.</span>
          </h1>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Live Opportunities" value={num(stats?.total_opportunities || 0)} sub="Across 20 US states" icon={Building2} />
        <KPI label="High Opportunity" value={num(stats?.high_opportunity_count || 0)} sub="AI Score ≥ 80" icon={TrendingUp} accent="#34d399" />
        <KPI label="Est. Portfolio Value" value={currency(stats?.total_estimated_value || 0)} sub="Sum of all assets" icon={DollarSign} />
        <KPI label="AI Categories" value={(stats?.by_category?.length || 0).toString()} sub="Distress types tracked" icon={Zap} accent="#7c9a85" />
      </div>

      {/* Main grid: Map + Top opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold tracking-tight">Nationwide Distress Map</h2>
            <Link to="/app/maps" data-testid="dashboard-fullmap-link" className="text-[11px] uppercase tracking-wider text-[#d4a574] hover:text-[#e8b988] flex items-center gap-1">
              Full Map <ArrowUpRight size={12} />
            </Link>
          </div>
          <MapView height={420} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-[#33291f] rounded-lg p-5 bg-[#1c1712]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-3">Distress by Category</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={catData} margin={{ top: 4, right: 4, left: 0, bottom: 40 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#7a6d5a" }} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: "#7a6d5a" }} />
                  <Tooltip contentStyle={{ background: "#241d16", border: "1px solid #33291f", fontSize: 12 }} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {catData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-[#33291f] rounded-lg p-5 bg-[#1c1712]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-3">State Activity Heat</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stateData} layout="vertical" margin={{ left: 8 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#7a6d5a" }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "#b8ac96" }} width={30} />
                  <Tooltip contentStyle={{ background: "#241d16", border: "1px solid #33291f", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#d4a574" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Right column: Top-scoring opps */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold tracking-tight">Top AI-Scored</h2>
            <div className="text-[10px] font-mono text-[#7a6d5a]">Live</div>
          </div>
          <div className="space-y-3">
            {top.slice(0, 6).map((o) => (
              <Link key={o.id} to={`/app/opportunities/detail/${o.id}`} data-testid={`top-opp-${o.id}`} className="flex gap-3 border border-[#33291f] rounded-lg overflow-hidden bg-[#1c1712] hover:border-[#d4a574]/40 group">
                <div className="w-24 shrink-0 aspect-square relative">
                  <img src={o.image_url} className="w-full h-full object-cover" alt="" />
                  <div className="absolute top-1 left-1 text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-[#14100c]/85" style={{ color: CATEGORY_META[o.category]?.color }}>
                    {CATEGORY_META[o.category]?.label?.split(" ")[0]}
                  </div>
                </div>
                <div className="flex-1 min-w-0 py-2.5 pr-3">
                  <div className="text-[13px] font-semibold line-clamp-1 group-hover:text-[#d4a574]">{o.title}</div>
                  <div className="text-[11px] text-[#7a6d5a] mt-0.5">{o.city}, {o.state}</div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[11px] font-mono text-[#d4a574]">{currency(o.price)}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#241d16] text-[#34d399]">{Math.round(o.ai_score?.opportunity)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Recent activity feed */}
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight mb-4">Today's Opportunities</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {recent.slice(0, 8).map((o) => <OpportunityCard key={o.id} opp={o} />)}
        </div>
      </div>
    </div>
  );
}
